import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Send, Users, Inbox, Mic, BookOpen, UserCircle, Globe, Search, GraduationCap,
} from 'lucide-react';
import { AdminSearchPalette } from './AdminSearchPalette';
import { useAuth } from '../../context/AuthContext';
import { NOTIF_DATA_EVENT } from './AdminNotificationCenter';

const LINKS = [
  { id: 'admin', to: '/admin', label: 'Pannello Admin', icon: LayoutDashboard, match: (p) => p === '/admin' },
  { id: 'messages', to: '/admin?tab=messaging', label: 'Messaggi', icon: Send, badge: 'unread_messages' },
  { id: 'groups', to: '/admin?tab=groups', label: 'Gruppi', icon: Users },
  { id: 'leads', to: '/admin?tab=leads', label: 'Lead', icon: Inbox, badge: 'new_leads_count' },
  { id: 'cms', to: '/admin/phonemes', label: 'Phoneme CMS', icon: Mic, match: (p) => p.startsWith('/admin/phonemes') || p.startsWith('/admin/help') || p.startsWith('/admin/audio-studio') },
  { id: 'lms', to: '/lms/phonemes', label: 'Libreria LMS', icon: BookOpen, match: (p) => p.startsWith('/lms') },
  { id: 'lectures', to: '/admin/lectures', label: 'Lectures', icon: GraduationCap, match: (p) => p.startsWith('/admin/lectures') },
  { id: 'members', to: '/area-clienti', label: 'Area Clienti', icon: UserCircle, match: (p) => p === '/area-clienti' },
  { id: 'site', to: '/', label: 'Sito pubblico', icon: Globe, match: (p) => p === '/' },
];

const useBodyOffset = (active) => {
  useEffect(() => {
    document.body.classList.toggle('has-admin-bar', active);
    return () => document.body.classList.remove('has-admin-bar');
  }, [active]);
};

const useNotifCounts = () => {
  const [counts, setCounts] = useState({});
  useEffect(() => {
    const onData = (e) => setCounts(e.detail || {});
    window.addEventListener(NOTIF_DATA_EVENT, onData);
    return () => window.removeEventListener(NOTIF_DATA_EVENT, onData);
  }, []);
  return counts;
};

const BarLink = ({ link, active, count }) => {
  const Icon = link.icon;
  return (
    <Link
      to={link.to}
      className={`relative flex items-center gap-1.5 px-2.5 h-7 rounded-md text-xs font-medium whitespace-nowrap transition-colors ${active ? 'bg-amber-500 text-slate-950' : 'text-slate-300 hover:text-white hover:bg-white/10'}`}
      data-testid={`admin-bar-${link.id}`}
      title={link.label}
    >
      <Icon className="w-3.5 h-3.5" />
      <span className="hidden md:inline">{link.label}</span>
      {count > 0 && (
        <span className="min-w-[16px] h-4 px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center" data-testid={`admin-bar-${link.id}-count`}>
          {count > 99 ? '99+' : count}
        </span>
      )}
    </Link>
  );
};

const useSearchShortcut = (enabled, setOpen) => {
  useEffect(() => {
    if (!enabled) return undefined;
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setOpen((o) => !o); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled, setOpen]);
};

const SearchTrigger = ({ onClick }) => (
  <button
    onClick={onClick}
    className="ml-auto flex items-center gap-2 h-7 px-2.5 rounded-md bg-white/5 border border-white/10 text-slate-300 hover:text-white hover:border-amber-500/50 transition-colors flex-shrink-0"
    data-testid="admin-bar-search-btn"
    title="Ricerca rapida (Ctrl+K)"
  >
    <Search className="w-3.5 h-3.5" />
    <span className="hidden sm:inline text-xs">Cerca utenti, gruppi, lead, risorse…</span>
    <kbd className="hidden lg:inline text-[10px] text-slate-400 border border-white/10 rounded px-1">Ctrl K</kbd>
  </button>
);

export const AdminTopBar = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const isAdmin = user?.role === 'admin';
  const counts = useNotifCounts();
  const [searchOpen, setSearchOpen] = useState(false);
  useBodyOffset(isAdmin);
  useSearchShortcut(isAdmin, setSearchOpen);
  if (!isAdmin) return null;

  return (
    <div style={{ top: 0 }} className="fixed inset-x-0 z-[70] h-9 bg-slate-950 border-b border-amber-500/30 flex items-center" data-testid="admin-top-bar">
      <div className="max-w-[1600px] w-full mx-auto px-3 flex items-center gap-2">
        <span className="hidden lg:inline text-[10px] font-bold uppercase tracking-[0.2em] text-amber-400 pr-2 border-r border-white/10">Admin</span>
        <nav className="flex items-center gap-1 overflow-x-auto no-scrollbar">
          {LINKS.map((l) => (
            <BarLink key={l.id} link={l} active={l.match ? l.match(pathname) : false} count={l.badge ? counts[l.badge] : 0} />
          ))}
        </nav>
        <SearchTrigger onClick={() => setSearchOpen(true)} />
      </div>
      <AdminSearchPalette open={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
  );
};
